import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Subject, Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, StandardViewModel, StandardService, Int32StringTuple, StandardGroupService, StandardGroupViewModel } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-standard-details',
    templateUrl: './standard-details.component.html',
    styleUrls: ['./standard-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StandardDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate {

    @ViewChild('form', { static: true }) form: NgForm;

    standard: StandardViewModel;
    standardGroup: StandardGroupViewModel = null;
    users$: Observable<Array<StringStringTuple>>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;
    noOfStandards: number;
    pageTitle: string = "Standarddetaljer";

    id: number;
    standardGroupId: number;
    standardForm: UntypedFormGroup;
    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private standardGroupService: StandardGroupService,
        private standardService: StandardService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('standardId'));
        this.standardGroupId = Number.parseInt(this.route.snapshot.paramMap.get('standardGroupId'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();


        if (this.id) {
            this.standardService.getStandardById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {

            this.standardService.getStandardCountByStandardGroup(this.standardGroupId).subscribe(t => this.noOfStandards = t, error => this.alertService.showStickyMessage(error));

            this.standardGroupService.getStandardGroupById(this.standardGroupId).subscribe(ag => this.standardGroupLoaded(ag),
                error => this.alertService.showStickyMessage(error));

            this.isNew = true;
            this.standard = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.standard) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.standard = this.initializeEmptyObject();
        }

        this.resetForm();
    }

    standardGroupLoaded(standardGroupViewModel: StandardGroupViewModel): void {
        this.standardGroup = standardGroupViewModel;
    }

    dataLoaded(standard: StandardViewModel) {
        this.standard = standard;
        this.standardGroupId = standard.standardGroupId;
        this.noOfStandards = standard.noOfStandards;

        this.standardGroupService.getStandardGroupById(this.standardGroupId).subscribe(ag => this.standardGroupLoaded(ag),
            error => this.alertService.showStickyMessage(error));

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.standardForm.controls[name];
    }


    private buildForm() {
        this.standardForm = this.formBuilder.group({
            standardId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            sortOrder: ['', Validators.required],
            enabled: '',
            description: '',
            suggestions: '',
            internalNotes: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.standard) {
            this.isNew = true;
            this.standard = this.initializeEmptyObject();
        }

        this.standardForm.reset({
            standardId: this.standard.standardId || '',
            name: this.standard.name || '',
            sortOrder: this.standard.sortOrder || '',
            enabled: this.standard.enabled || '',
            description: this.standard.description || '',
            suggestions: this.standard.suggestions || '',
            internalNotes: this.standard.internalNotes || '',
            createdBy: this.standard.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.standard.createdDate, true) || '',
            updatedBy: this.standard.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.standard.updatedDate, true) || '',
        });

        if (this.standard.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.standard.createdBy).pipe(map(t => t.item2));
        }
        if (this.standard.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.standard.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): StandardViewModel {
        var result = <StandardViewModel>{};
        result.enabled = true;
        result.standardGroupId = this.standardGroupId;

        return result;
    }

    private prepareSave(): StandardViewModel {
        const formModel = this.standardForm.value;

        return {
            standardId: this.isNew ? null : formModel.standardId,
            standardGroupId: this.standardGroupId,
            name: formModel.name,
            sortOrder: formModel.sortOrder,
            enabled: formModel.enabled ? true : false,
            description: formModel.description,
            suggestions: formModel.suggestions,
            internalNotes: formModel.internalNotes,
        };
    }

    public save() {
        if (!this.standardForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const standard = this.prepareSave();

        if (this.isNew) {
            this.standardService.createStandard(standard).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.standardService.updateStandard(standard.standardId, standard).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentally if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.standardForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }

    private saveCompleted(standard: StandardViewModel) {
        this.standard = standard;
        this.id = standard.standardId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), standard.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    getPageTitle(): string {
        if (this.isNew) {
            return "Ny standard för standardgrupp: " + (this.standardGroup != null ? this.standardGroup.name : "");
        } else {
            return "Standard " + (this.standard == null ? "" : this.standard.name) + " för standardgrupp: " + (this.standardGroup != null ? this.standardGroup.name : "");
        }
    }

    getSortOrderItems(): Array<number> {
        let itemCount = this.noOfStandards;
        if (!this.id) {
            itemCount += 1;
        }

        let result = new Array<number>(itemCount);
        for (let i = 0; i < itemCount; i++) {
            result[i] = i + 1;
        }
        return result;
    }

}
